import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import globalStore from '@/services/global.state';
import {defaultCurrencySymbol} from '@/config';
import {goTo} from '@/utils';
import {
  GlobalNotificationPayload,
  GlobalNotificationType,
} from './global-notification.type';

export type {GlobalNotificationPayload} from './global-notification.type';

const waveCashbackIcon = require('@/assets/imgs/notification/gift.webp');

/** 默认展示时长 */
const DEFAULT_DURATION_MS = 8000;
/** 进出场动画时长，需与 class transition 一致 */
const ANIMATION_MS = 280;

function getNotificationPreset(
  type: GlobalNotificationType,
  t: (key: string) => string,
) {
  switch (type) {
    case 'wave-cashback':
    default:
      return {
        line1: t('globalNotification.waveCashback.line1'),
        line2Prefix: t('globalNotification.waveCashback.line2Prefix'),
        line2Suffix: t('globalNotification.waveCashback.line2Suffix'),
        iconSrc: waveCashbackIcon as string,
      };
  }
}

const GlobalNotification = () => {
  const {t} = useTranslation();
  const [payload, setPayload] = useState<GlobalNotificationPayload | null>(
    null,
  );
  const [visible, setVisible] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  const closeAnimationTimerRef = useRef<number | null>(null);
  const payloadRef = useRef<GlobalNotificationPayload | null>(null);
  const closingRef = useRef(false);
  const payloadGenerationRef = useRef(0);

  const clearCloseTimer = () => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const clearCloseAnimationTimer = () => {
    if (closeAnimationTimerRef.current != null) {
      window.clearTimeout(closeAnimationTimerRef.current);
      closeAnimationTimerRef.current = null;
    }
  };
  const hide = (invokeClose = true) => {
    if (closingRef.current) {
      return;
    }
    closingRef.current = true;
    clearCloseTimer();
    setVisible(false);
    const current = payloadRef.current;
    const generation = payloadGenerationRef.current;
    closeAnimationTimerRef.current = window.setTimeout(() => {
      closeAnimationTimerRef.current = null;
      if (payloadGenerationRef.current !== generation) {
        return;
      }
      setPayload(null);
      payloadRef.current = null;
      closingRef.current = false;
      if (invokeClose) {
        current?.onClose?.();
      }
    }, ANIMATION_MS);
  };

  const show = (next: GlobalNotificationPayload) => {
    clearCloseTimer();
    clearCloseAnimationTimer();
    payloadGenerationRef.current += 1;
    closingRef.current = false;
    payloadRef.current = next;
    setVisible(false);
    setPayload(next);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setVisible(true);
      });
    });
    const durationMs =
      next.durationMs == null ? DEFAULT_DURATION_MS : next.durationMs;
    if (durationMs > 0) {
      closeTimerRef.current = window.setTimeout(() => {
        hide(true);
      }, durationMs);
    }
  };

  const handleContentPress = () => {
    const current = payloadRef.current;
    hide(false);
    current?.onPress?.();
    goTo('Notification');
  };

  const handleClosePress = (e: React.MouseEvent) => {
    e.stopPropagation();
    hide(true);
  };

  useEffect(() => {
    const sub = globalStore.globalNotificationSubject.subscribe(next => {
      show(next || {});
    });
    const unbindAccountReset = globalStore.registerPopupAccountResetHandler(
      () => {
        if (payloadRef.current) {
          hide(false);
        }
      },
    );
    return () => {
      sub.unsubscribe();
      unbindAccountReset();
      clearCloseTimer();
      clearCloseAnimationTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!payload) {
    return null;
  }

  const preset = getNotificationPreset(payload.type ?? 'wave-cashback', t);
  const amountText = `${defaultCurrencySymbol}${
    payload.amount == null || payload.amount === '' ? 'XX' : payload.amount
  }`;
  const line1 = payload.line1 ?? preset.line1;
  const line2Prefix = payload.line2Prefix ?? preset.line2Prefix;
  const line2Suffix = payload.line2Suffix ?? preset.line2Suffix;
  const iconSrc = payload.iconSrc || preset.iconSrc;

  return (
    <div
      className="pointer-events-none fixed left-0 right-0 z-[12000] flex justify-center px-4"
      style={{top: 'max(0.75rem, env(safe-area-inset-top, 0px))'}}>
      <div
        className={[
          'pointer-events-auto flex w-full max-w-[21.3125rem] cursor-pointer items-center gap-[1.125rem]',
          'rounded-xl border border-[rgba(255,255,255,0.1)] bg-[rgba(80,79,80,0.6)]',
          'px-2 py-2.5',
          'backdrop-blur-[0.18125rem] transition-[transform,opacity] duration-[280ms] ease-out',
          // 弹出：从上到下；关闭：从下到上收回
          visible
            ? 'translate-y-0 opacity-100'
            : '-translate-y-[120%] opacity-0',
        ].join(' ')}
        onClick={handleContentPress}
        role="status">
        <div className="flex min-w-0 flex-1 items-center gap-5">
          <div className="size-[2.625rem] shrink-0 overflow-hidden rounded-lg bg-gradient-to-b from-[#82cbff] to-white">
            <img src={iconSrc} alt="" className="h-full w-full object-cover" />
          </div>
          <div className="min-w-0 flex-1 font-semibold text-white">
            <p className="m-0 break-words text-xs leading-normal">{line1}</p>
            <p className="m-0 break-words text-xs leading-normal">
              <span>{line2Prefix}</span>
              <span className="text-sm text-[#ffbc00]">
                {amountText}
                {line2Suffix}
              </span>
            </p>
          </div>
        </div>
        <button
          type="button"
          aria-label={t('globalNotification.close')}
          className="relative flex h-[17px] w-[15px] shrink-0 items-center justify-center border-0 bg-transparent p-0 text-xl font-normal leading-none text-[#ddd] before:absolute before:-inset-3 before:content-['']"
          onClick={handleClosePress}>
          <span className="pointer-events-none">×</span>
        </button>
      </div>
    </div>
  );
};

export default GlobalNotification;
